import { Skeleton, Stack } from '@mui/material';
import * as Sentry from '@sentry/react';
import { useEffect, useRef, useState } from 'react';
import CallIcon from '@mui/icons-material/Call';
import { matchIsValidTel } from 'mui-tel-input';
import { FormProvider, useForm } from 'react-hook-form';

import femaleAudioFile from '../../../assets/female_sample_voice.wav';
import { AnalyticsEvents } from '../../../enums/AnalyticsEvents';
import {
  agentStatusToLabel,
  getAgentStatusColor,
  removeWhiteSpaces,
} from '../../../helpers/agentHelper';
import { useAnalytics } from '../../../hooks/useAnalytics';
import useLoadAgentImage from '../../../hooks/useLoadAgentImage';
import { useTranslation } from '../../../hooks/useTranslation';
import { useIsFeatureEnabled } from '../../../hooks/useIsFeatureEnabled';
import { AgentStatus, AgentVoice } from '../../../interfaces/Agent';
import { FeatureGate } from '../../../interfaces/FeatureGate';
import {
  isValidPhoneNumberPrefix,
  getDefaultPhoneInitialValue,
} from '../../../interfaces/Country';
import { testAgent } from '../../../services/agentService';
import StyledAgentImage from '../../../styles/StyledAgentImage';
import StyledForm from '../../../styles/StyledForm';
import VoiceAudioWaves from '../Animations/VoiceAudioWaves';
import Button from '../Button/Button';
import ButtonIcon from '../ButtonIcon/ButtonIcon';
import OnlineIcon from '../Icons/OnlineIcon';
import PlayIcon from '../Icons/PlayIcon';
import StopIcon from '../Icons/StopIcon';
import StyledIcon from '../Icons/StyledIcon';
import Modal from '../Modal/Modal';
import TelephoneInput from '../TelephoneInput/TelephoneInput';
import TextTruncated from '../TextTruncated/TextTruncated';
import StyledAgentName from './StyledAgentName';

const statusSwitcher = (currentStatus: AgentStatus) => {
  switch (currentStatus) {
    case AgentStatus.IN_REVIEW:
      return AgentStatus.ONLINE;
    case AgentStatus.ONLINE:
      return AgentStatus.OFFLINE;
    case AgentStatus.OFFLINE:
      return AgentStatus.ONLINE;
    default:
      Sentry.captureMessage(
        'Something went wrong, and the agent status failed to update.',
        'log'
      );
      return AgentStatus.OFFLINE;
  }
};

type AgentProfileAvatarProps = {
  agentId?: string;
  aiAgentImage?: string;
  aiAgentVoice?: string;
  agentName?: string;
  agentStatus?: AgentStatus;
  hideAgentName?: boolean;
  isPlaying: boolean;
  playAgentVoice: () => void;
  showAgentStatus?: boolean;
  width?: string;
  voice?: AgentVoice;
  showTestButton?: boolean;
};

const AgentProfileAvatar: React.FC<AgentProfileAvatarProps> = ({
  agentId,
  aiAgentImage,
  aiAgentVoice = femaleAudioFile,
  agentName = 'Demo',
  agentStatus = AgentStatus.IN_REVIEW,
  hideAgentName = false,
  isPlaying,
  playAgentVoice,
  showAgentStatus = true,
  width = '100%',
  showTestButton = false,
  voice,
}) => {
  const [status, setStatus] = useState<AgentStatus>(agentStatus);
  const audioDemoRef = useRef<HTMLAudioElement | null>(null);
  const { imgSrc } = useLoadAgentImage(aiAgentImage as string, voice);
  const methods = useForm({ mode: 'onChange', criteriaMode: 'all' });
  const [testModalOpen, setTestModalOpen] = useState(false);
  const buttonDisabledTimeout = useRef<NodeJS.Timeout | null>(null);
  const [modalButtonDisabled, setModalButtonDisabled] = useState(false);
  const { watch, formState } = methods;
  const phoneValue = watch('user_phone');
  const { track } = useAnalytics();
  const { t } = useTranslation();
  const hasUSFISupport = useIsFeatureEnabled(
    FeatureGate.US_FI_PHONE_NUMBERS_SUPPORT
  );

  const handleSetTestModalOpen = (open: boolean) => {
    setTestModalOpen(open);
    if (open) {
      track(AnalyticsEvents.AGENT_TEST_MODAL_OPENED, {
        agentId,
        source: 'agent_profile_page',
      });
      track(AnalyticsEvents.AGENT_TEST_BUTTON_CLICKED, {
        agentId,
        source: 'agent_profile_page',
      });
    } else {
      track(AnalyticsEvents.AGENT_TEST_MODAL_CLOSED, {
        agentId,
        source: 'agent_profile_page',
      });
    }
  };

  const handlePlayAgentVoice = () => {
    track(AnalyticsEvents.AGENT_TEST_BUTTON_CLICKED, {
      agentId,
      type: 'voiceDemo',
      source: 'agent_profile_page',
    });
    playAgentVoice();
  };

  const executeTestCall = async (event: any) => {
    event.preventDefault();

    const value = methods.getValues('user_phone');
    if (!value) return;
    if (!agentId) return;

    // Use dynamic phone number validation
    if (!isValidPhoneNumberPrefix(value, hasUSFISupport)) {
      return;
    }

    if (!matchIsValidTel(value)) return;

    track(AnalyticsEvents.AGENT_TEST_CALL_ATTEMPTED, {
      agentId,
      phone_number: value,
      timestamp: Date.now(),
      source: 'agent_profile_page',
    });
    try {
      const result = await testAgent({
        id: agentId,
        phone_number: removeWhiteSpaces(value),
      });
      if (result?.status === 'success') {
        track(AnalyticsEvents.AGENT_TEST_CALL_SUCCESS, {
          agentId,
          phone_number: value,
          timestamp: Date.now(),
          source: 'agent_profile_page',
        });
      } else {
        track(AnalyticsEvents.AGENT_TEST_CALL_FAILURE, {
          agentId,
          phone_number: value,
          error: result?.error,
          timestamp: Date.now(),
          source: 'agent_profile_page',
        });
      }
    } catch (error: any) {
      track(AnalyticsEvents.AGENT_TEST_CALL_FAILURE, {
        agentId,
        phone_number: value,
        error: error?.message,
        timestamp: Date.now(),
        source: 'agent_profile_page',
      });
    }
    setTestModalOpen(false);
    setModalButtonDisabled(true);

    const now = Date.now();
    localStorage.setItem(`agent_test_call_timestamp`, now.toString());
  };

  useEffect(() => {
    if (isPlaying && audioDemoRef.current) {
      audioDemoRef.current.currentTime = 0;
      audioDemoRef.current.play();
    } else {
      audioDemoRef.current?.pause();
    }
  }, [isPlaying]);

  useEffect(() => {
    if (modalButtonDisabled) {
      buttonDisabledTimeout.current = setTimeout(() => {
        setModalButtonDisabled(false);
      }, 120000);
    }

    return () => {
      buttonDisabledTimeout.current &&
        clearTimeout(buttonDisabledTimeout.current);
    };
  }, [modalButtonDisabled]);

  useEffect(() => {
    try {
      if (typeof window !== 'undefined' && window.localStorage) {
        const savedTimestamp = localStorage.getItem(
          `agent_test_call_timestamp`
        );
        if (savedTimestamp) {
          const elapsed = Date.now() - parseInt(savedTimestamp, 10);
          if (elapsed < 120000) {
            setModalButtonDisabled(true);
            const remaining = 120000 - elapsed;
            buttonDisabledTimeout.current = setTimeout(() => {
              setModalButtonDisabled(false);
              try {
                localStorage.removeItem(`agent_test_call_timestamp`);
              } catch (error) {
                console.error('Error removing localStorage item:', error);
              }
            }, remaining);
          } else {
            try {
              localStorage.removeItem(`agent_test_call_timestamp`);
            } catch (error) {
              console.error('Error removing localStorage item:', error);
            }
          }
        }
      }
    } catch (error) {
      console.error('Error accessing localStorage:', error);
    }
  }, []);

  return (
    <>
      <Stack position="relative" minHeight="400px">
        {!hideAgentName && (
          <StyledAgentName>
            <TextTruncated title={agentName} width={width}>
              {agentName}
            </TextTruncated>
          </StyledAgentName>
        )}
        {imgSrc ? (
          <StyledAgentImage
            src={imgSrc as string}
            alt={t('Ai Agent')}
            width={width}
          />
        ) : (
          <Stack>
            <Skeleton
              data-testid="agent-profile-avatar-skeleton"
              variant="rounded"
              width="400px"
              height="400px"
            />
          </Stack>
        )}
        <VoiceAudioWaves isPlaying={isPlaying} />
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          marginTop="10px"
        >
          {showTestButton ? (
            <Modal
              modalShouldBeOpen={testModalOpen}
              setChangeStatusModalOpen={handleSetTestModalOpen}
              buttonDisabled={modalButtonDisabled}
              mainPromptText={t('Your agent will call you in just 30 seconds!')}
              denyText={t('Close')}
              description={t('Enter your phone number')}
              openModalButtonText={t('Test Agent')}
              openModalButtonVariant="contained"
              openModalButtonColor="primary"
              openModalIcon={
                <div style={{ marginRight: '10px', marginTop: '7px' }}>
                  <CallIcon fontSize="small" />
                </div>
              }
            >
              <FormProvider {...methods}>
                <StyledForm onSubmit={executeTestCall} marginTop="0">
                  <TelephoneInput
                    initialValue={getDefaultPhoneInitialValue(hasUSFISupport)}
                    name="user_phone"
                    required
                  />
                  <Button
                    type="submit"
                    disabled={!formState.isValid || modalButtonDisabled}
                  >
                    {t('Test Call')}
                  </Button>
                </StyledForm>
              </FormProvider>
            </Modal>
          ) : (
            <ButtonIcon
              label={agentName}
              icon={isPlaying ? StopIcon : PlayIcon}
              onClick={handlePlayAgentVoice}
              position="relative"
            />
          )}
          {showAgentStatus && (
            <StyledIcon
              gap="0.5rem"
              textTransform="uppercase"
              color={getAgentStatusColor(status)}
            >
              <OnlineIcon
                width={16}
                height={16}
                fill={getAgentStatusColor(status)}
              />
              {agentStatusToLabel(status, t)}
            </StyledIcon>
          )}
        </Stack>
        <audio
          ref={audioDemoRef}
          src={aiAgentVoice}
          onEnded={playAgentVoice}
        ></audio>
      </Stack>
    </>
  );
};

export default AgentProfileAvatar;
